import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IConfigCliente, INotificaciones, IToken, IUsuario } from 'modelos/src';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { api } from '../../../environments/environment';
import { HelperService } from '../../auxiliares/helper.service';
import { WebSocketService } from '../../auxiliares/websocket';

// Tope de espera de la restauración de sesión al arrancar. Si la API tarda más,
// la app arranca sin sesión y el usuario ve el login.
const TIMEOUT_RESTAURAR_MS = 5000;

// Sesión compartida entre pestañas (P40). Antes cada pestaña guardaba su token
// en sessionStorage: una pestaña nueva recuperaba la sesión por cookie, eso
// rotaba el par de tokens y dejaba sin token a la que ya estaba abierta, que al
// recuperarse le hacía lo mismo a la primera, sin fin. Ahora las pestañas del
// mismo usuario comparten un solo par (localStorage, por usuario, si entró con
// "Recordarme"; sin Recordarme la sesión sigue siendo de la pestaña) y se turnan
// para refrescarlo. Cada pestaña recuerda en sessionStorage qué usuario es, así
// dos usuarios distintos en el mismo navegador no se pisan.
const CLAVE_SESION = (idUsuario: string) => `gas-sesion:${idUsuario}`;
const CLAVE_ULTIMO_USUARIO = 'gas-ultimo-usuario';
// null: pestaña nueva (toma al último usuario que entró con Recordarme).
// '': la pestaña cerró sesión (no toma a nadie hasta el próximo login).
const CLAVE_USUARIO_PESTANA = 'gas-usuario-pestana';
const CLAVE_PERSISTENTE = 'gas-sesion-persistente';

// Basura en el storage (editado a mano, versión vieja) cuenta como "sin
// sesión": un JSON.parse que tira acá rompería el arranque de la app.
function parsear<T>(raw: string | null): T | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export type MotivoCierre =
  | 'usuario-inactivo'
  | 'usuario-borrado'
  | 'cliente-inactivo'
  | 'sesion-cerrada';

const MOTIVOS: string[] = [
  'usuario-inactivo',
  'usuario-borrado',
  'cliente-inactivo',
  'sesion-cerrada',
];

interface ISesionGuardada {
  token: string;
  refreshToken: string;
  usuario: IUsuario;
}

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  constructor(
    private http: HttpClient,
    private helper: HelperService,
    private webSocketService: WebSocketService,
    private router: Router,
  ) {
    // Otras pestañas preguntan si alguien sigue usando una sesión antes de
    // borrarla (ver olvidarSesionSiNadieLaUsa).
    LoginService.canal?.addEventListener('message', (e) => {
      if (
        e.data?.tipo === 'hay-alguien' &&
        e.data.id === LoginService.idPestana()
      ) {
        LoginService.canal?.postMessage({ tipo: 'aca', id: e.data.id });
      }
      // Otra pestaña del mismo usuario recibió la revocación.
      if (
        e.data?.tipo === 'cerrar' &&
        e.data.idUsuario === LoginService.idPestana()
      ) {
        this.cerrarSesion(e.data.motivo, false);
      }
    });
    this.webSocketService.sesionRevocada$.subscribe((motivo) =>
      this.cerrarSesion(motivo as MotivoCierre, true),
    );
  }

  /**
   * Emite en cada cierre forzado. Estático para que ListadosService y
   * NavigationService limpien su estado sin inyectar LoginService.
   */
  public static sesionCerrada$ = new Subject<void>();

  /**
   * Cierra la sesión y manda al login con el motivo. Con `todasLasPestanas`
   * también cierra las otras pestañas de este usuario (no las de otro usuario
   * del mismo navegador). El aviso va antes de borrar la sesión: así las demás
   * pestañas ya cerraron cuando llegue el 'hay-alguien' de removeLoginInfo.
   */
  public cerrarSesion(motivo: MotivoCierre, todasLasPestanas: boolean) {
    // Llega del server o de otra pestaña: desconocido o ausente = genérico.
    if (!MOTIVOS.includes(motivo)) motivo = 'sesion-cerrada';
    const idUsuario = LoginService.idPestana();
    if (todasLasPestanas && idUsuario) {
      LoginService.canal?.postMessage({ tipo: 'cerrar', idUsuario, motivo });
    }
    this.removeLoginInfo();
    LoginService.sesionCerrada$.next();
    this.webSocketService.closeWS();
    this.router.navigate(['/login'], { queryParams: { motivo } });
  }

  public tieneTelefono = false;
  public tieneEmail = false;

  public getUser: BehaviorSubject<IUsuario> = new BehaviorSubject<IUsuario>(
    LoginService.getUsuario(),
  );

  public isLogged() {
    return !!LoginService.getToken();
  }
  //
  public static getToken() {
    return LoginService.leerSesion()?.token || '';
  }

  public getRefreshToken(): string {
    return LoginService.leerSesion()?.refreshToken || '';
  }

  // ALMACÉN DE SESIÓN

  /** Usuario de esta pestaña, o null si no tiene sesión. */
  private static idPestana(): string | null {
    let id = sessionStorage.getItem(CLAVE_USUARIO_PESTANA);
    if (id === null) {
      LoginService.migrarSesionVieja();
      id = sessionStorage.getItem(CLAVE_USUARIO_PESTANA);
    }
    if (id === null) {
      // Pestaña nueva: entra con el último usuario que usó Recordarme en este
      // navegador, sin pedirle nada a la API (pedirlo rotaría su sesión).
      const ultimo = localStorage.getItem(CLAVE_ULTIMO_USUARIO);
      if (ultimo && localStorage.getItem(CLAVE_SESION(ultimo))) {
        sessionStorage.setItem(CLAVE_USUARIO_PESTANA, ultimo);
        sessionStorage.setItem(CLAVE_PERSISTENTE, '1');
        id = ultimo;
      }
    }
    return id || null;
  }

  private static almacen(): Storage {
    return sessionStorage.getItem(CLAVE_PERSISTENTE) === '1'
      ? localStorage
      : sessionStorage;
  }

  private static canal =
    typeof BroadcastChannel === 'undefined'
      ? undefined
      : new BroadcastChannel('gas-sesion');

  /**
   * Borra la sesión compartida de `id` salvo que otra pestaña abierta la siga
   * usando: cerrar sesión en una pestaña no saca a las demás (es lo que se hace
   * para entrar con otro usuario en otra pestaña). Aunque quede, ninguna
   * pestaña nueva la toma, porque ya no es la del último usuario.
   */
  private static olvidarSesionSiNadieLaUsa(id: string) {
    const clave = CLAVE_SESION(id);
    const canal = LoginService.canal;
    if (!canal) {
      localStorage.removeItem(clave);
      return;
    }
    let enUso = false;
    const escuchar = (e: MessageEvent) => {
      if (e.data?.tipo === 'aca' && e.data.id === id) enUso = true;
    };
    canal.addEventListener('message', escuchar);
    canal.postMessage({ tipo: 'hay-alguien', id });
    setTimeout(() => {
      canal.removeEventListener('message', escuchar);
      if (!enUso) localStorage.removeItem(clave);
    }, 300);
  }

  private static sesionRaw?: string | null;
  private static sesionParseada?: ISesionGuardada;

  private static leerSesion(): ISesionGuardada | undefined {
    const id = LoginService.idPestana();
    const raw = id ? LoginService.almacen().getItem(CLAVE_SESION(id)) : null;
    if (raw !== LoginService.sesionRaw) {
      LoginService.sesionRaw = raw;
      LoginService.sesionParseada = parsear<ISesionGuardada>(raw);
    }
    return LoginService.sesionParseada;
  }

  private static escribirSesion(sesion: ISesionGuardada) {
    const id = sessionStorage.getItem(CLAVE_USUARIO_PESTANA);
    if (!id) return;
    LoginService.almacen().setItem(CLAVE_SESION(id), JSON.stringify(sesion));
  }

  /**
   * Las pestañas abiertas antes de P40 tienen la sesión en las claves viejas
   * de sessionStorage. Siguen como sesión de la pestaña: desde acá no se sabe
   * si el usuario tildó Recordarme, y pasarlas a localStorage recordaría en
   * una PC compartida a quien no lo tildó. Si tenía cookie, el primer refresh
   * que falle la recupera y ahí sí pasa a la compartida (restaurarSesion).
   */
  private static migrarSesionVieja() {
    const token = sessionStorage.getItem('token');
    const refreshToken = sessionStorage.getItem('refreshToken') || '';
    const usuario = parsear<IUsuario>(sessionStorage.getItem('usuario'));
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('usuario');
    if (!token || !usuario?._id) return;
    sessionStorage.setItem(
      CLAVE_SESION(usuario._id),
      JSON.stringify({ token, refreshToken, usuario }),
    );
    sessionStorage.setItem(CLAVE_USUARIO_PESTANA, usuario._id);
    sessionStorage.setItem(CLAVE_PERSISTENTE, '0');
  }

  /**
   * Se llama desde templates y helpers de permisos (a veces una vez por fila),
   * o sea en cada ciclo de detección de cambios. El usuario trae la config
   * entera del cliente y parsearlo cada vez era trabajo fijo en todas las
   * pantallas: se memoiza contra el string guardado, así cualquier `setItem`
   * invalida sola la copia. Devuelve una referencia compartida: quien la
   * modifique tiene que persistirla (como hace `actualizarConfigClienteCacheado`).
   */
  public static getUsuario(): IUsuario {
    return LoginService.leerSesion()?.usuario as IUsuario;
  }

  // Relee el par recién antes de escribir: si otra pestaña lo acaba de
  // rotar, escribir la copia memoizada lo pisaría con uno ya borrado.
  public static setUsuario(usuario: IUsuario) {
    const id = LoginService.idPestana();
    if (!id) return;
    const sesion = parsear<ISesionGuardada>(
      LoginService.almacen().getItem(CLAVE_SESION(id)),
    );
    if (sesion) LoginService.escribirSesion({ ...sesion, usuario });
  }

  // Cierra la sesión en esta pestaña. Las otras pestañas del mismo usuario
  // siguen (igual que antes de compartir la sesión).
  public removeLoginInfo() {
    const id = sessionStorage.getItem(CLAVE_USUARIO_PESTANA);
    const persistente = sessionStorage.getItem(CLAVE_PERSISTENTE) === '1';
    sessionStorage.setItem(CLAVE_USUARIO_PESTANA, '');
    sessionStorage.removeItem(CLAVE_PERSISTENTE);
    if (!id) return;
    if (!persistente) {
      sessionStorage.removeItem(CLAVE_SESION(id));
      return;
    }
    if (localStorage.getItem(CLAVE_ULTIMO_USUARIO) === id) {
      localStorage.removeItem(CLAVE_ULTIMO_USUARIO);
    }
    LoginService.olvidarSesionSiNadieLaUsa(id);
  }

  // `persistente`: la sesión se comparte con las otras pestañas del navegador
  // (Recordarme). Sin el argumento se mantiene el modo que ya tenía la pestaña.
  public setLoginInfo(
    token: IToken,
    persistente = sessionStorage.getItem(CLAVE_PERSISTENTE) === '1',
  ) {
    if (token.user?.datosPersonales?.telefono) {
      this.tieneTelefono = true;
    } else {
      this.tieneTelefono = false;
    }
    this.tieneEmail = !!token.user?.datosPersonales?.email;
    const id = token.user?._id;
    if (!id) return;
    sessionStorage.setItem(CLAVE_USUARIO_PESTANA, id);
    sessionStorage.setItem(CLAVE_PERSISTENTE, persistente ? '1' : '0');
    LoginService.escribirSesion({
      token: token.accessToken!,
      refreshToken: token.refreshToken || '',
      usuario: token.user!,
    });
    if (persistente) localStorage.setItem(CLAVE_ULTIMO_USUARIO, id);
  }

  /**
   * Refresca la `config` del cliente cacheada en la sesión.
   *
   * Los gates de configuración global (`moduloCoberturaLorawan.activo`,
   * `ciclosFacturacion`, etc.) se leen del usuario cacheado, no de la API. Sin esto, una
   * config que el usuario acaba de guardar no surte efecto hasta el próximo login: el
   * caso concreto es el día de cierre de facturación, donde el selector de período
   * aparecía recién después de recargar.
   *
   * Pisa sólo `config` y no el `cliente` entero: el cliente del login trae campos que
   * `GET /clientes/propio` no necesariamente devuelve igual.
   */
  public actualizarConfigClienteCacheado(config?: IConfigCliente) {
    const usuario = LoginService.getUsuario();
    if (!usuario?.cliente) return;
    usuario.cliente.config = config;
    LoginService.setUsuario(usuario);
    this.getUser.next(usuario);
  }

  // Después de cambiar algo del usuario propio: cache, suscriptores y avisos.
  public actualizarUsuario(u: IUsuario) {
    LoginService.setUsuario(u);
    this.getUser.next(u);
    this.tieneTelefono = !!u?.datosPersonales?.telefono;
    this.tieneEmail = !!u?.datosPersonales?.email;
  }

  // Link del mail de confirmación: público, sin token.
  public verificarEmail(token: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${api}/usuarios/email/verificar`, { token }),
    );
  }

  public updateNotificaciones(notificaciones: INotificaciones[]) {
    const usuario = LoginService.getUsuario();
    usuario.notificaciones = notificaciones;
    LoginService.setUsuario(usuario);
    this.getUser.next(usuario);
  }

  // LOGIN

  // withCredentials es imprescindible: sin él el navegador descarta el
  // Set-Cookie de la respuesta, porque el front y la API son orígenes distintos.
  public _login(
    username: string,
    password: string,
    recordarme = false,
  ): Observable<IToken> {
    return this.http.post<IToken>(
      `${api}/auth/login`,
      { username, password, recordarme },
      { withCredentials: true },
    );
  }

  // Canjea la cookie httpOnly de sesión por un token nuevo. El refresh token
  // vive sólo en esa cookie: el JS no lo ve nunca, así que un XSS no puede
  // llevárselo (a diferencia de guardarlo en localStorage).
  private _session(): Observable<IToken> {
    return this.http.post<IToken>(
      `${api}/auth/session`,
      {},
      { withCredentials: true },
    );
  }

  public _logoutSesionPersistida(): Observable<void> {
    return this.http.post<void>(
      `${api}/auth/logout`,
      {},
      { withCredentials: true },
    );
  }

  public _forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${api}/passwordresets/forgot`, {
      email,
    });
  }

  public _resetPassword(token: string, password: string): Observable<void> {
    return this.http.post<void>(`${api}/passwordresets/reset`, {
      token,
      password,
    });
  }

  public async login(
    username: string,
    password: string,
    recordarme = false,
  ): Promise<IToken> {
    try {
      const token = await firstValueFrom(
        this._login(username, password, recordarme),
      );
      this.setLoginInfo(token!, recordarme);
      this.webSocketService.initWs();
      return token!;
    } catch (error) {
      this.helper.notifError(error);
      throw error;
    }
  }

  /**
   * Restaura la sesión desde la cookie httpOnly, si el usuario marcó
   * "Recordarme" en su último login. Es lo que permite abrir un deep link en una
   * pestaña nueva sin volver a loguearse: `sessionStorage` muere con la pestaña,
   * la cookie no.
   *
   * Devuelve el token si pudo, o undefined si no hay sesión persistida. No
   * notifica errores: no tener cookie es el caso normal, no una falla.
   *
   * La cookie es una sola por navegador (la del último login con Recordarme):
   * si la pestaña es de otro usuario (`idEsperado`), el token no se adopta.
   */
  public async restaurarSesion(
    idEsperado?: string,
  ): Promise<IToken | undefined> {
    try {
      const token = await firstValueFrom(this._session());
      if (!token?.accessToken) return undefined;
      if (idEsperado && token.user?._id !== idEsperado) return undefined;
      this.setLoginInfo(token, true);
      this.webSocketService.asegurarConexion();
      await this.enriquecerUsuario();
      return token;
    } catch {
      return undefined;
    }
  }

  // Corre una sola vez al arrancar la app, antes de que el router decida la
  // ruta: así un deep link con sesión persistida entra directo a destino en vez
  // de rebotar por el login. Si ya hay token (propio o compartido por otra
  // pestaña), no hace nada: pedir uno nuevo rotaría la sesión de las demás.
  public async restaurarSesionAlArrancar(): Promise<void> {
    if (LoginService.getToken()) return;
    // Cota defensiva: si la API no responde, la app tiene que arrancar igual y
    // caer en el login, no quedarse en blanco esperando.
    const conTimeout = new Promise<undefined>((resolve) =>
      setTimeout(() => resolve(undefined), TIMEOUT_RESTAURAR_MS),
    );
    await Promise.race([this.restaurarUnaVez(), conTimeout]);
  }

  // Al reabrir el navegador varias pestañas arrancan juntas con la misma
  // cookie: si todas la canjean, la primera rota y las demás quedan con una
  // cookie ya usada y caen al login. Se canjea de a una; las que esperaban
  // encuentran la sesión compartida y no piden nada.
  private async restaurarUnaVez(): Promise<IToken | undefined> {
    const restaurar = async () =>
      LoginService.getToken() ? undefined : this.restaurarSesion();
    return navigator.locks
      ? navigator.locks.request('gas-restaurar-sesion', restaurar)
      : restaurar();
  }

  public async forgotPassword(email: string): Promise<void> {
    try {
      await firstValueFrom(this._forgotPassword(email));
    } catch (error) {
      this.helper.notifError(error);
      throw error;
    }
  }

  public async resetPassword(token: string, password: string): Promise<void> {
    try {
      await firstValueFrom(this._resetPassword(token, password));
    } catch (error) {
      this.helper.notifError(error);
      throw error;
    }
  }

  // Obtiene el usuario propio con todos los datos del cliente (evita dep. circular con UsuariosService)
  private _listarPropio(): Observable<IUsuario> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${LoginService.getToken()}`,
    });
    return this.http.get<IUsuario>(`${api}/usuarios/propio`, { headers });
  }

  public async enriquecerUsuario(): Promise<void> {
    try {
      const usuario = await firstValueFrom(this._listarPropio());
      LoginService.setUsuario(usuario);
    } catch {
      // No bloqueante
    }
  }

  // REFRESH TOKEN

  // withCredentials también acá: la API reescribe la cookie de sesión con el
  // refresh token nuevo (el viejo queda revocado al rotar), y sin la cookie en
  // el request no puede saber que hay sesión persistida.
  private _refreshToken(refresh_token: string): Observable<IToken> {
    return this.http.post<IToken>(
      `${api}/auth/refresh_token`,
      { refresh_token },
      { withCredentials: true },
    );
  }

  /**
   * Un solo refresh a la vez entre todas las pestañas del usuario: rotar mata
   * el token anterior, así que el primero rota y los demás, al entrar, ya
   * encuentran el token nuevo y lo usan. Sin Web Locks (navegadores viejos)
   * queda el chequeo de adentro, que cubre la mayoría de los casos.
   */
  // `tokenFallido`: el token con el que salió el pedido que dio 401. Si al
  // entrar al turno el token compartido ya es otro, alguien lo renovó.
  public async refreshToken(
    tokenFallido = LoginService.getToken(),
  ): Promise<IToken> {
    const id = LoginService.idPestana();
    const tokenPrevio = tokenFallido;
    const refrescar = () => this.refrescarSinCompetir(tokenPrevio, id);
    if (id && navigator.locks) {
      return navigator.locks.request(`gas-refresh:${id}`, refrescar);
    }
    return refrescar();
  }

  private sesionComoToken(): IToken {
    return {
      accessToken: LoginService.getToken(),
      refreshToken: this.getRefreshToken(),
      user: LoginService.getUsuario(),
    } as IToken;
  }

  private async refrescarSinCompetir(
    tokenPrevio: string,
    id: string | null,
  ): Promise<IToken> {
    // Otra pestaña ya lo renovó mientras esperábamos el turno.
    const tokenActual = LoginService.getToken();
    if (tokenActual && tokenActual !== tokenPrevio) {
      this.webSocketService.asegurarConexion();
      return this.sesionComoToken();
    }
    try {
      const refresh_token = this.getRefreshToken();
      const token = await firstValueFrom(this._refreshToken(refresh_token));
      this.setLoginInfo(token);
      this.webSocketService.asegurarConexion();
      await this.enriquecerUsuario();
      return token;
      // "Invalid grant: refresh token is invalid"
    } catch (error) {
      // Sin Web Locks otra pestaña pudo rotar en paralelo: si el token cambió,
      // el refresh que falló era el viejo y la sesión sigue viva.
      const tokenNuevo = LoginService.getToken();
      if (tokenNuevo && tokenNuevo !== tokenPrevio) {
        this.webSocketService.asegurarConexion();
        return this.sesionComoToken();
      }
      // Última chance antes de mandar al login: la sesión persistida en cookie.
      // Una pestaña sin usuario (cerró sesión) no la prueba: entraría con el
      // usuario dueño de la cookie.
      const restaurada = id ? await this.restaurarSesion(id) : undefined;
      if (restaurada) return restaurada;

      const e = error as { error?: { codigo?: string; motivo?: MotivoCierre } };
      if (e.error?.codigo === 'sesion-revocada') {
        this.cerrarSesion(e.error.motivo!, true);
      } else {
        this.cerrarSesion('sesion-cerrada', false);
      }
      // El login ya muestra el motivo: el pedido que disparó el refresh no
      // tiene que mostrar además el error crudo de gas-auth (notifError lo
      // ignora con esta marca).
      throw Object.assign(new Error('Sesión cerrada'), { sesionCerrada: true });
    }
  }
}
